import { showWarning } from "@/lib/api-client"
import { toast } from "sonner"

// Global rate limit dialog handler
let globalRateLimitDialogHandler: ((message: string) => void) | null = null

export function setGlobalRateLimitDialogHandler(handler: (message: string) => void) {
  globalRateLimitDialogHandler = handler
}

export class ErrorHandlerService {
  static handleAuthError(): void {
    showWarning("Your API key has expired. Please check your LLM provider settings.");
  }

  static handleRateLimitError(customMessage?: string): void {
    // Show popup dialog for rate limit
    if (globalRateLimitDialogHandler && customMessage) {
      globalRateLimitDialogHandler(customMessage)
    } else {
      // Fallback: show toast
      const friendly =
        customMessage ||
        "Too many requests. Please wait a few seconds and try again.";
      showWarning("Rate limit reached", friendly);
    }
  }

  static handleServerError(): void {
    showWarning("Server error occurred. Please try again later.");
  }

  static handleGenericError(error: Error): void {
    showWarning(
      "Failed to get AI response. Please try again.", 
      error.message ? `Error: ${error.message}` : undefined
    );
  }

  static handleChatError(error: Error): void {
    console.error("Chat error:", error);

    const errorMessage = error.message?.toLowerCase() || '';

    if (errorMessage.includes('401') || errorMessage.includes('unauthorized')) {
      this.handleAuthError();
    } else if (errorMessage.includes('429') || errorMessage.includes('rate limit')) {
      // Parse JSON error response if present
      let actualMessage = error.message;
      try {
        const jsonMatch = error.message.match(/\{.*\}/);
        if (jsonMatch) {
          const errorObj = JSON.parse(jsonMatch[0]);
          actualMessage = errorObj.message || error.message;
        }
      } catch (e) {
        // If parsing fails, use original message
        actualMessage = error.message;
      }
      this.handleRateLimitError(actualMessage);
    } else if (errorMessage.includes('500') || errorMessage.includes('502') || errorMessage.includes('503')) {
      this.handleServerError();
    } else {
      this.handleGenericError(error);
    }
  }

  static handleMessageError(content: string, onRemoveMessage: () => void): void {
    if (content.includes("Error:") && content.includes("401")) {
      toast.error("Your API key is invalid or expired. Please check your LLM provider settings.");
      onRemoveMessage();
    } else if (content.includes("Error:") && content.includes("429")) {
      toast.error("Rate limit reached", {
        description: "Too many requests. Please wait a few seconds and try again.",
      });
      onRemoveMessage();
    } else if (content.includes("Error:") && (content.includes("500") || content.includes("502") || content.includes("503"))) {
      toast.error("Server error occurred. Please try again later.");
      onRemoveMessage();
    } else if (content.startsWith("Error:") && content.length > 10) {
      toast.error("An error occurred. Please try again.");
      onRemoveMessage();
    }
  }
} 